/**
 * qty-discount.js — Dock Builders Theme
 *
 * Quantity-based price discount widget for the `product.qty-discount` template.
 *
 * Differences from dock-ladder-depot version:
 *   - Price block ID: `Price-{sectionId}` (capital P)
 *   - Variant change event: `variant-change` via webvista.subscribe()
 *   - Variant payload: event.data.variant
 *
 * TIERS — read from product metafield `custom.qty_tiers` (JSON).
 *   Set per-product in Admin → Products → [product] → Metafields.
 *   Format: [{ "minQty": 2, "maxQty": 4, "pct": 5 }, ...]
 *   The full-price row (qty 1) is always prepended automatically.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Tier helpers
  // ---------------------------------------------------------------------------

  function getTiers() {
    const widget = getWidget();
    if (!widget) return [];
    try {
      const metafieldTiers = JSON.parse(widget.dataset.tiers || '[]');
      return [{ minQty: 1, maxQty: 1, pct: 0 }, ...metafieldTiers];
    } catch (e) {
      return [{ minQty: 1, maxQty: 1, pct: 0 }];
    }
  }

  function getTierForQty(qty) {
    const tiers = getTiers();
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) return tiers[i];
    }
    return tiers[0];
  }

  // ---------------------------------------------------------------------------
  // Money formatter
  // ---------------------------------------------------------------------------

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.Shopify.money_format || '${{amount}}'
      );
    }
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ---------------------------------------------------------------------------
  // DOM queries — always fresh (price block innerHTML is replaced on variant swap)
  // ---------------------------------------------------------------------------

  function getWidget()     { return document.getElementById('qty-discount-widget'); }
  function getSectionId()  { const w = getWidget(); return w ? w.dataset.sectionId : null; }

  function getQtyInput() {
    // dock-builders buy_buttons uses name="quantity" inside the product form
    const sectionId = getSectionId();
    if (!sectionId) return null;
    const form = document.getElementById('product-form-' + sectionId);
    return form ? form.querySelector('[name="quantity"]') : null;
  }

  function getPriceBlock() {
    // Capital P — dock-builders uses id="Price-{sectionId}"
    const sectionId = getSectionId();
    return sectionId ? document.getElementById('Price-' + sectionId) : null;
  }

  function getBasePriceCents() {
    const w = getWidget();
    return w ? (parseInt(w.dataset.basePrice, 10) || 0) : 0;
  }

  // ---------------------------------------------------------------------------
  // Price DOM manipulation
  // ---------------------------------------------------------------------------

  function applyPriceDiscount(discountPct) {
    const priceBlock     = getPriceBlock();
    const basePriceCents = getBasePriceCents();
    if (!priceBlock || !basePriceCents) return;

    const priceEl     = priceBlock.querySelector('.price');
    const regularItem = priceBlock.querySelector('.price-regular .price-item-regular, .price__regular .price-item--regular');
    if (!priceEl || !regularItem) return;

    if (discountPct === 0) {
      priceEl.classList.remove('price--on-sale', 'price-on-sale');
      regularItem.textContent = formatMoney(basePriceCents);
      const injected = priceBlock.querySelector('.qty-discount-sale-block');
      if (injected) injected.remove();
      return;
    }

    const discountedCents = Math.round(basePriceCents * (1 - discountPct / 100));

    let saleBlock = priceBlock.querySelector('.qty-discount-sale-block');
    if (!saleBlock) {
      saleBlock = document.createElement('div');
      saleBlock.className = 'price-sale qty-discount-sale-block';
      saleBlock.innerHTML =
        '<span>' +
          '<span class="visually-hidden">Regular price</span>' +
          '<s class="price-item price-item-regular qty-discount-compare"></s>' +
        '</span>' +
        '<span class="visually-hidden">Sale price</span>' +
        '<span class="price-item price-item-sale price-item-last qty-discount-sale-price"></span>';

      const container = priceBlock.querySelector('.price__container, .price-container');
      if (container) container.appendChild(saleBlock);
      else priceBlock.appendChild(saleBlock);
    }

    priceEl.classList.add('price--on-sale', 'price-on-sale');
    regularItem.textContent = formatMoney(discountedCents);
    saleBlock.querySelector('.qty-discount-compare').textContent    = formatMoney(basePriceCents);
    saleBlock.querySelector('.qty-discount-sale-price').textContent = formatMoney(discountedCents);
  }

  function updateTable(activeTier) {
    const widget = getWidget();
    if (!widget) return;

    const basePriceCents = getBasePriceCents();

    widget.querySelectorAll('.qty-discount-row').forEach((row) => {
      const min    = parseInt(row.dataset.minQty, 10);
      const max    = parseInt(row.dataset.maxQty, 10);
      const pct    = parseInt(row.dataset.pct, 10);
      const active = activeTier.minQty === min && activeTier.maxQty === max;

      row.classList.toggle('qty-discount-row--active', active);

      const priceCell = row.querySelector('.qty-discount-price');
      if (priceCell && basePriceCents) {
        priceCell.textContent = formatMoney(Math.round(basePriceCents * (1 - pct / 100)));
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Core update
  // ---------------------------------------------------------------------------

  function update() {
    const qtyInput = getQtyInput();
    const qty      = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    const tier     = getTierForQty(qty);
    applyPriceDiscount(tier.pct);
    updateTable(tier);
  }

  function wireQtyInput() {
    const qtyInput = getQtyInput();
    if (!qtyInput || qtyInput._qtyDiscountWired) return;
    qtyInput._qtyDiscountWired = true;
    qtyInput.addEventListener('change', update);
    qtyInput.addEventListener('input',  update);
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function init() {
    if (!getWidget()) return;

    wireQtyInput();
    update();

    // dock-builders uses webvista.subscribe for variant changes
    if (window.webvista && window.webvista.subscribe) {
      window.webvista.subscribe('variant-change', function (event) {
        const variant = event && event.data && event.data.variant;
        if (variant) {
          const widget = getWidget();
          if (widget) widget.dataset.basePrice = variant.price;
        }
        requestAnimationFrame(function () {
          wireQtyInput();
          update();
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
